import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Home, ArrowLeft, FileText, Check, Clock, X, ClipboardCheck } from 'lucide-react';

export default function FormsPage() {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fillingTemplate, setFillingTemplate] = useState(null);
  const [responses, setResponses] = useState({});
  const [viewingSubmission, setViewingSubmission] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [tRes, sRes] = await Promise.all([
        axios.get(`${API}/form-templates`),
        axios.get(`${API}/form-submissions/mine`)
      ]);
      setTemplates(tRes.data);
      setSubmissions(sRes.data);
    } catch (error) {
      toast.error('Failed to fetch forms');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpenTemplate = (t) => {
    setFillingTemplate(t);
    const initial = {};
    (t.fields || []).forEach(f => {
      initial[f.id] = f.type === 'checkbox' ? false : '';
    });
    setResponses(initial);
  };

  const handleSubmitForm = async () => {
    // Validate required
    for (const f of fillingTemplate.fields) {
      const val = responses[f.id];
      if (f.required && (val === '' || val === false || val === null || val === undefined)) {
        toast.error(`Please fill: ${f.label}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/form-submissions`, {
        template_id: fillingTemplate.id,
        responses: responses
      });
      toast.success('Form submitted successfully!');
      setFillingTemplate(null);
      setResponses({});
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit');
    } finally { setSubmitting(false); }
  };

  const handleReview = async (status) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/form-submissions/${viewingSubmission.id}/review`, {
        status, reviewer_notes: reviewNotes
      });
      toast.success(`Form ${status}`);
      setViewingSubmission(null);
      setReviewNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to update');
    } finally { setSubmitting(false); }
  };

  const renderField = (field) => {
    const val = responses[field.id];
    const setVal = (v) => setResponses({ ...responses, [field.id]: v });
    const key = `field-${field.id}`;

    if (field.type === 'textarea') {
      return <Textarea data-testid={key} value={val} onChange={(e) => setVal(e.target.value)} placeholder={field.placeholder} rows={3} />;
    }
    if (field.type === 'checkbox') {
      return (
        <label className="flex items-start gap-2 cursor-pointer">
          <input data-testid={key} type="checkbox" checked={!!val} onChange={(e) => setVal(e.target.checked)} className="mt-1 w-4 h-4" />
          <span className="text-sm">{field.label}{field.required && ' *'}</span>
        </label>
      );
    }
    if (field.type === 'select') {
      return (
        <Select value={val} onValueChange={setVal}>
          <SelectTrigger data-testid={key}><SelectValue placeholder="Select an option" /></SelectTrigger>
          <SelectContent>
            {(field.options || []).map((opt) => (
              <SelectItem key={opt} value={opt}>{opt}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (field.type === 'signature') {
      return <Input data-testid={key} value={val} onChange={(e) => setVal(e.target.value)} placeholder="Type full name" className="font-serif italic" />;
    }
    return <Input data-testid={key} type={field.type} value={val} onChange={(e) => setVal(e.target.value)} placeholder={field.placeholder} />;
  };

  const getStatusBadge = (status) => {
    const map = {
      submitted: { icon: Clock, cls: 'bg-yellow-100 text-yellow-700', label: 'Submitted' },
      reviewed: { icon: FileText, cls: 'bg-blue-100 text-blue-700', label: 'Reviewed' },
      approved: { icon: Check, cls: 'bg-green-100 text-green-700', label: 'Approved' },
      rejected: { icon: X, cls: 'bg-red-100 text-red-700', label: 'Rejected' },
    };
    const s = map[status] || map.submitted;
    const Icon = s.icon;
    return <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${s.cls}`}><Icon className="w-3 h-3" />{s.label}</span>;
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header bg-white/70 border-b border-border/40 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-3 sm:py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-4">
            <Button data-testid="back-btn" onClick={() => navigate(-1)} variant="outline" size="sm" className="rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" />Back
            </Button>
            <div className="flex items-center gap-2">
              <Home className="w-6 h-6 text-primary" />
              <span className="text-xl font-bold tracking-tight text-primary">PrimePocket</span>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3">
            <ClipboardCheck className="w-8 h-8 text-primary" />
            <h1 className="text-xl sm:text-2xl md:text-3xl font-medium tracking-tight">Forms & Verifications</h1>
          </div>
          <p className="text-muted-foreground mt-2">
            {user?.role === 'tenant' ? 'Fill out forms in-app to speed up your application.' : 'Review tenant submissions and manage forms.'}
          </p>
        </div>

        <Tabs defaultValue={user?.role === 'tenant' ? 'available' : 'submissions'}>
          <TabsList className="bg-muted mb-6">
            {user?.role === 'tenant' && (
              <TabsTrigger data-testid="tab-available" value="available" className="rounded-full px-6">
                Available Forms ({templates.length})
              </TabsTrigger>
            )}
            <TabsTrigger data-testid="tab-submissions" value="submissions" className="rounded-full px-6">
              {user?.role === 'tenant' ? 'My Submissions' : 'Submissions to Review'} ({submissions.length})
            </TabsTrigger>
          </TabsList>

          {user?.role === 'tenant' && (
            <TabsContent value="available">
              {loading ? (
                <div className="text-center py-20">Loading...</div>
              ) : templates.length === 0 ? (
                <Card className="p-20 text-center border-2 border-dashed">
                  <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                  <h3 className="text-xl font-medium">No forms available</h3>
                </Card>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {templates.map((t, idx) => (
                    <Card key={t.id} className="p-6 border border-border hover:shadow-md transition-shadow" data-testid={`template-${idx}`}>
                      <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                        <FileText className="w-6 h-6 text-primary" />
                      </div>
                      <h3 className="text-lg font-medium mb-2">{t.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{t.description}</p>
                      <div className="text-xs text-muted-foreground mb-4">{t.fields?.length || 0} fields</div>
                      <Button data-testid={`fill-form-${idx}-btn`} onClick={() => handleOpenTemplate(t)}
                        className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover">
                        Fill Out Form
                      </Button>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          <TabsContent value="submissions">
            {submissions.length === 0 ? (
              <Card className="p-20 text-center border-2 border-dashed">
                <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-xl font-medium">No submissions yet</h3>
              </Card>
            ) : (
              <div className="space-y-4">
                {submissions.map((s, idx) => (
                  <Card key={s.id} className="p-6 border border-border hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => setViewingSubmission(s)} data-testid={`submission-${idx}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-medium">{s.template_title}</h3>
                          {getStatusBadge(s.status)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {user?.role === 'landlord' ? `From: ${s.tenant_name}` : 'Submitted by you'}
                          {' • '}
                          {new Date(s.created_at).toLocaleDateString()}
                        </div>
                      </div>
                      <Button variant="outline" size="sm" className="rounded-full">View</Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Fill Form Dialog */}
      <Dialog open={!!fillingTemplate} onOpenChange={(v) => !v && setFillingTemplate(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {fillingTemplate && (
            <>
              <DialogHeader>
                <DialogTitle>{fillingTemplate.title}</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-muted-foreground mt-2">{fillingTemplate.description}</p>
              <div className="space-y-5 mt-6">
                {(fillingTemplate.fields || []).map((field) => (
                  <div key={field.id}>
                    {field.type !== 'checkbox' && (
                      <Label>{field.label}{field.required && ' *'}</Label>
                    )}
                    <div className="mt-2">{renderField(field)}</div>
                    {field.help_text && <p className="text-xs text-muted-foreground mt-1">{field.help_text}</p>}
                  </div>
                ))}
                <Button data-testid="submit-form-btn" onClick={handleSubmitForm} disabled={submitting}
                  className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground btn-hover">
                  {submitting ? 'Submitting...' : 'Submit Form'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* View Submission Dialog */}
      <Dialog open={!!viewingSubmission} onOpenChange={(v) => !v && setViewingSubmission(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {viewingSubmission && (
            <>
              <DialogHeader>
                <DialogTitle>{viewingSubmission.template_title}</DialogTitle>
              </DialogHeader>
              <div className="mt-2 flex items-center gap-3">
                {getStatusBadge(viewingSubmission.status)}
                <span className="text-sm text-muted-foreground">
                  Submitted by {viewingSubmission.tenant_name} on {new Date(viewingSubmission.created_at).toLocaleString()}
                </span>
              </div>
              <div className="space-y-4 mt-6">
                {Object.entries(viewingSubmission.responses).map(([key, value]) => (
                  <div key={key} className="border-b border-border pb-3">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">{key.replace(/_/g, ' ')}</div>
                    <div className="text-sm font-medium" data-testid={`response-${key}`}>
                      {typeof value === 'boolean' ? (value ? '✓ Yes' : '✗ No') : String(value || '—')}
                    </div>
                  </div>
                ))}
                {viewingSubmission.reviewer_notes && (
                  <div className="p-4 bg-muted rounded-lg">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Reviewer Notes</div>
                    <div className="text-sm">{viewingSubmission.reviewer_notes}</div>
                  </div>
                )}
                {user?.role === 'landlord' && viewingSubmission.landlord_id === user.id && viewingSubmission.status === 'submitted' && (
                  <div className="border-t border-border pt-6 space-y-3">
                    <div>
                      <Label>Review Notes (optional)</Label>
                      <Textarea data-testid="review-notes-textarea" value={reviewNotes}
                        onChange={(e) => setReviewNotes(e.target.value)} rows={3} className="mt-2" />
                    </div>
                    <div className="flex gap-3">
                      <Button data-testid="approve-form-btn" onClick={() => handleReview('approved')} disabled={submitting}
                        className="flex-1 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground">
                        <Check className="w-4 h-4 mr-2" />Approve
                      </Button>
                      <Button data-testid="reject-form-btn" onClick={() => handleReview('rejected')} disabled={submitting}
                        variant="destructive" className="flex-1 rounded-full">
                        <X className="w-4 h-4 mr-2" />Reject
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
